'use client';

/**
 * Agent Debug Feeds — SSE client hook.
 * Opens one EventSource to /api/debug/agent-stream?callId=, parses the
 * `history` and `debug` SSE events, exposes per-feed buffers + dynamic feed
 * discovery + connection state, reconnects with backoff.
 * See docs/AGENT_DEBUG_FEEDS_DESIGN.md §5.2.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AgentDebugEvent } from '@/lib/debug/types';

export type AgentDebugConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

const CLIENT_EVENT_CAP = 8000; // client-side ring cap (debug feed — keep generous)

export interface UseAgentDebugStreamResult {
  events: AgentDebugEvent[];
  byFeed: Map<string, AgentDebugEvent[]>; // keyed `${source}/${feed}`
  seenFeeds: Set<string>;
  connectionState: AgentDebugConnectionState;
  clear: () => void;
}

export function useAgentDebugStream(
  callId: string | null | undefined,
  opts?: { enabled?: boolean },
): UseAgentDebugStreamResult {
  const enabled = opts?.enabled !== false && !!callId;
  const [events, setEvents] = useState<AgentDebugEvent[]>([]);
  const [connectionState, setConnectionState] = useState<AgentDebugConnectionState>('idle');
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffRef = useRef(1000);
  const generationRef = useRef(0);

  const clear = useCallback(() => setEvents([]), []);

  const appendOne = useCallback((ev: AgentDebugEvent) => {
    setEvents((prev) => {
      const next = prev.length >= CLIENT_EVENT_CAP ? prev.slice(prev.length - CLIENT_EVENT_CAP + 1) : prev.slice();
      next.push(ev);
      return next;
    });
  }, []);

  const seedMany = useCallback((batch: AgentDebugEvent[]) => {
    setEvents((prev) => {
      const merged = [...prev, ...batch];
      // De-dup by id, keep order, cap.
      const seen = new Set<string>();
      const out: AgentDebugEvent[] = [];
      for (const e of merged) {
        if (!e || !e.id || seen.has(e.id)) continue;
        seen.add(e.id);
        out.push(e);
      }
      return out.length > CLIENT_EVENT_CAP ? out.slice(out.length - CLIENT_EVENT_CAP) : out;
    });
  }, []);

  useEffect(() => {
    // Tear down on disable / callId change.
    const closeCurrent = () => {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    if (!enabled || !callId) {
      closeCurrent();
      setConnectionState('idle');
      // Don't leave a previous call's persona / prompt data lying around in
      // memory when the dialog is disabled or the call changes.
      setEvents([]);
      return;
    }

    // New call (or re-enable): start from a clean buffer — the server will
    // replay this call's history via the `history` SSE frame.
    setEvents([]);

    const myGen = ++generationRef.current;
    backoffRef.current = 1000;

    const connect = () => {
      if (generationRef.current !== myGen) return;
      closeCurrent();
      setConnectionState('connecting');
      const es = new EventSource(`/api/debug/agent-stream?callId=${encodeURIComponent(callId)}`);
      esRef.current = es;

      es.addEventListener('history', (e: MessageEvent) => {
        if (generationRef.current !== myGen) return;
        setConnectionState('open');
        backoffRef.current = 1000; // successful connect → reset backoff
        try {
          const arr = JSON.parse(e.data) as AgentDebugEvent[];
          if (Array.isArray(arr)) seedMany(arr);
        } catch {
          /* ignore malformed history */
        }
      });

      es.addEventListener('debug', (e: MessageEvent) => {
        if (generationRef.current !== myGen) return;
        try {
          const ev = JSON.parse(e.data) as AgentDebugEvent;
          if (ev && ev.id) appendOne(ev);
        } catch {
          /* ignore malformed event */
        }
      });

      // `ping` keep-alives are ignored intentionally.

      es.onerror = () => {
        if (generationRef.current !== myGen) return;
        setConnectionState('error');
        es.close();
        if (esRef.current === es) esRef.current = null;
        // Backoff reconnect.
        const delay = Math.min(backoffRef.current, 30_000);
        backoffRef.current = Math.min(backoffRef.current * 2, 30_000);
        reconnectTimerRef.current = setTimeout(() => {
          if (generationRef.current === myGen) connect();
        }, delay);
      };
    };

    connect();
    return () => {
      // bump generation so any pending reconnect/handlers no-op
      generationRef.current++;
      closeCurrent();
      setConnectionState('closed');
    };
  }, [enabled, callId, appendOne, seedMany]);

  const { byFeed, seenFeeds } = useMemo(() => {
    const m = new Map<string, AgentDebugEvent[]>();
    const s = new Set<string>();
    for (const ev of events) {
      const key = `${ev.source}/${ev.feed}`;
      s.add(key);
      const list = m.get(key);
      if (list) list.push(ev);
      else m.set(key, [ev]);
    }
    return { byFeed: m, seenFeeds: s };
  }, [events]);

  return { events, byFeed, seenFeeds, connectionState, clear };
}
